import React from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <BackButton onClick={() => navigate(-1)} />
          <div className="text-center mt-4">
            <h1 className="text-3xl font-bold text-gray-900">BBA Portal – Privacy Policy</h1>
            <p className="text-gray-600 mt-2">Effective Date: July 1, 2025</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="prose prose-lg max-w-none">
            <div className="text-center mb-8">
              <p className="text-gray-700 mb-4">
                Builders Breakthrough Advisors ("BBA," "we," "our," or "us") respects your privacy and is committed to protecting the personal and business information you share with us. This Privacy Policy explains how we collect, use, store, and share information when you access the BBA Portal ("Service").
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
              <div className="text-gray-700 space-y-4">
                <h3 className="text-xl font-medium text-gray-900 mb-3">Personal Information</h3>
                <p>When you sign up or interact with the BBA Portal, we may collect:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your name and contact details (email, phone number)</li>
                  <li>Account login credentials</li>
                  <li>Company or business affiliation</li>
                  <li>Preferences or settings you configure within the Portal</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-6">Usage Data</h3>
                <p>We automatically collect certain information related to how you use the Service, such as:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>IP address, browser type, and access times</li>
                  <li>Device and operating system details</li>
                  <li>Pages visited and features used</li>
                  <li>Diagnostic and error data</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-6">Business Data</h3>
                <p>Users may upload or generate business-specific content in the portal, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Documents, spreadsheets, and files</li>
                  <li>Operational notes and internal benchmarks</li>
                  <li>Responses to assessments or learning activities</li>
                  <li>Custom dashboards or insights created during use</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <div className="text-gray-700 space-y-4">
                <p>We use your information to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide access to portal features and content</li>
                  <li>Deliver personalized dashboards, documents, and learning experiences</li>
                  <li>Support internal usage analytics and performance improvements</li>
                  <li>Communicate updates, reports, or support information</li>
                  <li>Enforce security measures and prevent misuse</li>
                  <li>Fulfill legal obligations and protect rights</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Information Sharing</h2>
              <div className="text-gray-700 space-y-4">
                <p>We do not sell or rent your information, including your mobile phone number. Your mobile phone number will not be shared with or sold to third parties for their marketing purposes. We may share data only in the following limited cases:</p>
                
                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">With Service Providers</h3>
                <p>We work with trusted third parties to support infrastructure, analytics, and communications. These providers are contractually obligated to protect your data.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">For Legal Compliance</h3>
                <p>We may disclose information as required by law or to comply with legal processes, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Subpoenas or court orders</li>
                  <li>Government investigations</li>
                  <li>Preventing fraud, harm, or unlawful activity</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Business Changes</h3>
                <p>If BBA is involved in a merger, acquisition, or sale of assets, relevant data may be transferred as part of the transaction under the same privacy protections.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Security</h2>
              <div className="text-gray-700 space-y-4">
                <p>We implement safeguards to protect your information, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Data encryption (in transit and at rest)</li>
                  <li>Access controls and user authentication</li>
                  <li>Secure cloud infrastructure</li>
                  <li>Ongoing monitoring and incident response</li>
                </ul>
                <p>Despite these measures, no system is completely secure. Use the Service with awareness of inherent risks associated with online platforms.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Retention</h2>
              <div className="text-gray-700 space-y-4">
                <p>We retain personal and business data only as long as necessary to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide and improve the Service</li>
                  <li>Comply with legal and regulatory requirements</li>
                  <li>Resolve disputes or enforce our agreements</li>
                </ul>
                <p>Upon account deletion, we will delete or anonymize your data unless retention is legally required.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
              <div className="text-gray-700 space-y-4">
                <p>Depending on your location, you may have rights including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access to your personal data</li>
                  <li>Correction of inaccuracies</li>
                  <li>Deletion of your account and related data</li>
                  <li>Restriction or objection to data processing</li>
                  <li>Request for data portability</li>
                </ul>
                <p>To exercise any of these rights, contact us using the information at the end of this policy.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Cookies and Tracking</h2>
              <div className="text-gray-700 space-y-4">
                <p>We use cookies and similar technologies to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Store user preferences</li>
                  <li>Improve performance and usability</li>
                  <li>Analyze platform engagement and usage trends</li>
                </ul>
                <p>You may disable cookies in your browser settings, though some features may become limited.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Third-Party Tools</h2>
              <div className="text-gray-700 space-y-4">
                <p>The BBA Portal may interface with third-party platforms (e.g., analytics tools or document hosting). These services have their own privacy policies. We are not responsible for their data practices, and recommend reviewing their terms directly.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
              <div className="text-gray-700 space-y-4">
                <p>The Service is not intended for children under 13. We do not knowingly collect data from children. If you believe a child has submitted personal information, contact us immediately.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. International Use</h2>
              <div className="text-gray-700 space-y-4">
                <p>If you access the Portal from outside the U.S., your data may be transferred to and stored in the United States or other jurisdictions with different data protection laws. We implement appropriate safeguards for international data handling.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
              <div className="text-gray-700 space-y-4">
                <p>We may update this Privacy Policy from time to time. If changes are significant, we will notify you through:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The BBA Portal interface</li>
                  <li>Email or direct message (if applicable)</li>
                </ul>
                <p>Continued use of the Service after changes become effective constitutes your acceptance.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. SMS Text Messaging</h2>
              <div className="text-gray-700 space-y-4">
                <p>By providing your mobile phone number and opting in to receive SMS text messages from BBA, you agree to the following terms regarding SMS communications:</p>
                
                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Mobile Number Privacy</h3>
                <p>We respect your privacy. Your mobile phone number will not be shared with, sold to, or rented to third parties for their marketing purposes. We may share your number only with service providers who assist us in delivering SMS messages, and only under strict confidentiality agreements.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Purpose of SMS Communications</h3>
                <p>When you opt in to receive SMS messages, we may send you:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Business alerts and notifications (e.g., appointment reminders, task updates)</li>
                  <li>Important account information and security alerts</li>
                  <li>Service updates and announcements</li>
                  <li>Promotional offers and marketing messages (only if you have opted in to marketing communications)</li>
                  <li>Support and customer service communications</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Message Frequency</h3>
                <p>Message frequency varies based on your interactions with the BBA Portal and your communication preferences. You may receive:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Service-related messages as needed (account updates, security alerts, etc.)</li>
                  <li>Up to 5 promotional or marketing messages per month (if you have opted in to marketing)</li>
                </ul>
                <p>You can adjust your communication preferences in your account settings at any time.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Message and Data Rates</h3>
                <p className="font-semibold text-gray-900">Message and data rates may apply.</p>
                <p>Standard message and data rates from your wireless carrier may apply to SMS messages sent and received. These charges are determined by your mobile carrier and appear on your monthly bill or are deducted from your prepaid balance. BBA is not responsible for any charges incurred as a result of SMS communications.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Opt-Out Instructions</h3>
                <p>You may opt out of receiving SMS messages at any time by:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Replying "STOP"</strong> to any SMS message from BBA</li>
                  <li>Replying "UNSUBSCRIBE" to any SMS message from BBA</li>
                  <li>Adjusting your notification preferences in your account settings within the BBA Portal</li>
                  <li>Contacting us directly at info@buildersbreakthrough.com or (919) 824-5744</li>
                </ul>
                <p>After opting out, you will receive a confirmation message. Please note that opting out of SMS messages may limit our ability to send you time-sensitive alerts or notifications. You may continue to receive service-related messages that are necessary for account management or required by law, even after opting out of promotional messages.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Opt-In and Consent</h3>
                <p>By providing your mobile phone number and opting in to SMS communications, you:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Consent to receive SMS messages from BBA at the number you provided</li>
                  <li>Confirm that you are the authorized user of the mobile phone number provided</li>
                  <li>Understand that consent is not a condition of purchase or use of the Service</li>
                  <li>Acknowledge that you can opt out at any time using the methods described above</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Supported Carriers</h3>
                <p>SMS messages are delivered through standard messaging services and are supported by all major U.S. wireless carriers, including but not limited to AT&T, T-Mobile, Verizon, Sprint, and others. Delivery of messages is subject to carrier availability and network coverage.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Help and Support</h3>
                <p>For SMS-related questions or assistance, you can:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Reply "HELP" to any SMS message from BBA</li>
                  <li>Contact us at info@buildersbreakthrough.com</li>
                  <li>Call us at (919) 824-5744</li>
                </ul>
                <p className="mt-4">For more information about our terms and policies, please review our <a href="/terms" className="text-blue-600 hover:text-blue-800 underline">Terms of Service</a>.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
              <div className="text-gray-700 space-y-4">
                <p>If you have questions about this Privacy Policy or how we handle your information, please contact:</p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    <strong>Email:</strong> info@buildersbreakthrough.com<br />
                    <strong>Address:</strong> 5102 Durham-Chapel Hill Blvd, Durham, NC 27707<br />
                    <strong>Phone:</strong> (919) 824-5744
                  </p>
                </div>
                <p className="mt-4">For questions about SMS communications specifically, you can also reply "HELP" to any SMS message from BBA.</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 